import React from "react"
import {Coins} from 'lucide-react'
import type { PointsSetting } from "./siteSettingsTypes"

export default function SiteSettingsPointsTab(props: {
  pointsSetting: PointsSetting | null
  pointsPerDollar: number
  minPointsToUse: number
  pointsIsActive: boolean
  pointsDescription: string
  setPointsPerDollar: (n: number) => void
  setMinPointsToUse: (n: number) => void
  setPointsIsActive: (v: boolean) => void
  setPointsDescription: (v: string) => void
}) {
  const {
    pointsSetting,
    pointsPerDollar,
    minPointsToUse,
    pointsIsActive,
    pointsDescription,
    setPointsPerDollar,
    setMinPointsToUse,
    setPointsIsActive,
    setPointsDescription
  } = props

  const examplePoints = 1000
  const exampleDollar = pointsPerDollar > 0 ? examplePoints / pointsPerDollar : 0

  return (
    <div className="mb-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div className="bg-gray-950 border border-gray-800 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Coins size={24} className="text-blue-400" />
              <h3 className="text-xl font-light tracking-wider">點數折扣設定</h3>
            </div>

            <div className="space-y-6">
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pointsIsActive}
                    onChange={(e) => setPointsIsActive(e.target.checked)}
                    className="w-5 h-5 bg-gray-900 border border-gray-700 checked:bg-blue-600 checked:border-blue-600 cursor-pointer"
                  />
                  <div>
                    <div className="text-gray-100 font-medium">啟用點數折扣功能</div>
                    <div className="text-xs text-gray-500">關閉後會員無法在結帳時使用點數</div>
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  點數兌換比例 <span className="text-red-400">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={pointsPerDollar}
                    onChange={(e) => setPointsPerDollar(Number(e.target.value))}
                    min={1}
                    className="flex-1 px-4 py-3 bg-gray-900 border border-gray-700 text-gray-100 focus:outline-none focus:border-blue-500"
                  />
                  <span className="text-gray-400 whitespace-nowrap">分 = NT$ 1</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">例如：設定為 100，表示 100 點 = NT$ 1 元</div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  最低使用點數門檻 <span className="text-red-400">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={minPointsToUse}
                    onChange={(e) => setMinPointsToUse(Number(e.target.value))}
                    min={0}
                    className="flex-1 px-4 py-3 bg-gray-900 border border-gray-700 text-gray-100 focus:outline-none focus:border-blue-500"
                  />
                  <span className="text-gray-400">分</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">會員至少需要此數量的點數才能使用折扣</div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">設定說明（選填）</label>
                <textarea
                  value={pointsDescription}
                  onChange={(e) => setPointsDescription(e.target.value)}
                  rows={3}
                  placeholder="輸入此設定的說明或備註..."
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-gray-100 focus:outline-none focus:border-blue-500 resize-none"
                />
                {pointsSetting ? <div className="text-xs text-gray-600 mt-2">目前使用中記錄：{pointsSetting._id}</div> : null}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-gray-950 border border-gray-800 p-6">
            <h3 className="text-xl font-light tracking-wider mb-4">當前設定摘要</h3>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-gray-800">
                <span className="text-gray-400">功能狀態</span>
                <span className={`font-medium ${pointsIsActive ? "text-green-400" : "text-red-400"}`}>
                  {pointsIsActive ? "✓ 已啟用" : "✕ 已停用"}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-800">
                <span className="text-gray-400">兌換比例</span>
                <span className="text-gray-100">{pointsPerDollar} 分 = NT$ 1</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-400">最低使用</span>
                <span className="text-gray-100">{minPointsToUse} 分</span>
              </div>
            </div>
          </div>

          <div className="bg-blue-950 border border-blue-800 p-6">
            <h3 className="text-xl font-light tracking-wider mb-4 text-blue-100">使用示例</h3>
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-blue-200 mb-2">📊 點數價值計算：</div>
                <div className="bg-blue-900 bg-opacity-30 p-3 rounded">
                  <div className="text-blue-100">
                    會員有 <span className="font-bold">{examplePoints.toLocaleString()}</span> 點
                  </div>
                  <div className="text-blue-300 text-xs mt-1">
                    = NT$ <span className="font-bold">{exampleDollar.toLocaleString()}</span> 元折扣
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-950 border border-gray-800 p-6">
            <h3 className="text-xl font-light tracking-wider mb-4">⚠️ 注意事項</h3>
            <ul className="space-y-2 text-sm text-gray-400">
              <li className="flex items-start gap-2">
                <span className="text-gray-600">•</span>
                <span>修改設定後會立即生效，影響所有新訂單</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gray-600">•</span>
                <span>停用功能後，會員仍可累積點數但無法使用</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
